import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Upload, FileText, Image as ImageIcon, Download, Trash2, FileIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MedicalFilesPanel = ({ patientId = null, readOnly = false, pollInterval = 0, onNewFile = null }) => {
  const { t, language } = useLanguage();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const inputRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const onNewFileRef = useRef(onNewFile);
  useEffect(() => { onNewFileRef.current = onNewFile; }, [onNewFile]);

  const fetchFilesRef = useRef(null);

  const fetchFiles = async () => {
    try {
      const url = patientId ? `${API}/files?patient_id=${patientId}` : `${API}/files`;
      const r = await axios.get(url, { withCredentials: true });
      const incoming = r.data || [];

      // Detect new files vs. the previous snapshot. Skip the first run (initial load shouldn't toast).
      if (initializedRef.current && onNewFileRef.current) {
        const fresh = incoming.filter((f) => !knownIdsRef.current.has(f._id));
        fresh.forEach((f) => onNewFileRef.current(f));
      }
      knownIdsRef.current = new Set(incoming.map((f) => f._id));
      initializedRef.current = true;

      setFiles(incoming);
    } catch (e) {
      // ignore
    }
  };
  fetchFilesRef.current = fetchFiles;

  useEffect(() => {
    // Reset detection state when target patient changes
    knownIdsRef.current = new Set();
    initializedRef.current = false;
    if (fetchFilesRef.current) fetchFilesRef.current();

    if (!pollInterval || pollInterval <= 0) return undefined;
    const id = setInterval(() => { if (fetchFilesRef.current) fetchFilesRef.current(); }, pollInterval);
    return () => clearInterval(id);
  }, [patientId, pollInterval]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setMsg({ type: 'error', text: t('الملف كبير جداً (الحد الأقصى 10 ميجا)', 'File too large (max 10MB)', 'Fichier trop grand') });
      return;
    }
    setUploading(true);
    setMsg({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);

    try {
      await axios.post(`${API}/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMsg({ type: 'success', text: t('تم رفع الملف بنجاح', 'File uploaded successfully', 'Fichier téléchargé') });
      setDescription('');
      if (inputRef.current) inputRef.current.value = '';
      fetchFiles();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('فشل الرفع', 'Upload failed', 'Échec') });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const r = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ type: 'error', text: t('فشل التحميل', 'Download failed', 'Échec') });
    }
  };

  const handleView = async (fileId) => {
    try {
      const r = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (e) {
      setMsg({ type: 'error', text: t('فشل العرض', 'View failed', 'Échec') });
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm(t('حذف هذا الملف؟', 'Delete this file?', 'Supprimer?'))) return;
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      fetchFiles();
    } catch (e) {
      setMsg({ type: 'error', text: t('فشل الحذف', 'Delete failed', 'Échec') });
    }
  };

  const getIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <ImageIcon size={20} className="text-blue-600" />;
    if (contentType === 'application/pdf') return <FileText size={20} className="text-red-600" />;
    return <FileIcon size={20} className="text-gray-600" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-[#2c3e50]">
        {t('المستندات الطبية', 'Medical Documents', 'Documents médicaux')}
      </h3>

      {msg.text && (
        <Alert variant={msg.type === 'error' ? 'destructive' : 'default'} className={msg.type === 'success' ? 'bg-green-50 border-green-200' : ''}>
          <AlertDescription className={msg.type === 'success' ? 'text-green-800' : ''}>{msg.text}</AlertDescription>
        </Alert>
      )}

      {!readOnly && (
        <Card className="p-4 border-2 border-dashed border-[#1e88e5] bg-blue-50">
          <div className="text-center">
            <Upload size={32} className="mx-auto mb-2 text-[#1e88e5]" />
            <p className="font-semibold text-[#2c3e50] mb-1">
              {t('ارفع مستنداتك الطبية', 'Upload Medical Documents', 'Téléchargez vos documents')}
            </p>
            <p className="text-sm text-gray-600 mb-3">
              {t('PDF أو صور (X-ray، تحاليل) — حد أقصى 10 ميجا', 'PDF or images (X-rays, lab results) — max 10MB', 'PDF ou images — max 10MB')}
            </p>
            <Input
              placeholder={t('وصف الملف (اختياري)', 'File description (optional)', 'Description (optionnel)')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mb-3 bg-white"
              data-testid="file-description-input"
            />
            <input
              ref={inputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
              disabled={uploading}
              className="hidden"
              id="file-upload-input"
              data-testid="file-upload-input"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-[#1e88e5] hover:bg-[#1565c0]"
              data-testid="select-file-button"
            >
              {uploading ? t('جاري الرفع...', 'Uploading...', 'Téléchargement...') : t('اختر ملف', 'Choose File', 'Choisir')}
            </Button>
          </div>
        </Card>
      )}

      {files.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-gray-600">{t('لا توجد ملفات', 'No files yet', 'Aucun fichier')}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <Card key={f._id} className="p-3" data-testid={`file-${f._id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getIcon(f.content_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{f.original_filename}</p>
                  {f.description && <p className="text-xs text-gray-500 truncate">{f.description}</p>}
                  <p className="text-xs text-gray-400">{formatSize(f.size)} • {new Date(f.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleView(f._id)} data-testid={`view-file-${f._id}`}>
                    {t('عرض', 'View', 'Voir')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(f._id, f.original_filename)} data-testid={`download-file-${f._id}`}>
                    <Download size={16} />
                  </Button>
                  {!readOnly && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(f._id)} className="text-red-500" data-testid={`delete-file-${f._id}`}>
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
